import ApiCollection                from '@widesk-core/models/ApiCollection';
import BatchManager                 from '@widesk-core/models/batch/BatchManager';
import FeatureModel                 from '@models/map/FeatureModel';
import MapModel                     from '@models/map/MapModel';
import PlaceModel                   from '@models/map/PlaceModel';
import ProjectVersionModel          from '@models/map/ProjectVersionModel';
import currentProjectEditorStore    from '@/TokamapEditor/stores/currentProjectEditorStore';
import { featureAttributes }        from '@/TokamapEditor/helpers/restoredMapContent';
import { loadPublishedDocuments }   from '@/TokamapEditor/stores/PreviewMapsStore';
import { pairMapsWithDocuments }    from '@/TokamapEditor/stores/pairMapsWithDocuments';
import { restoredConfiguration }    from '@/TokamapEditor/helpers/restoredMapContent';

/** La garde par défaut d'`ApiCollection` (1000) vise des listes affichées ; ici on veut la totalité. */
const MAX_FEATURES_PER_MAP = 10000;
const MAX_PLACES_PER_PROJECT = 10000;

export type RestoreReport = {
	maps: number;
	deleted: number;
	created: number;
	/** Formes revenues sans leur lien vers un point d'intérêt, supprimé depuis la publication. */
	unlinked: number;
	/** Cartes dont le cadrage n'a pas pu être rétabli, leurs formes l'ayant été. */
	configurationsFailed: number;
};

/**
 * Remet le projet ouvert dans l'état d'une publication, en repartant de son fichier condensé. Ni les
 * cartes, ni les points d'intérêt, ni les thèmes du projet ne sont touchés.
 *
 * Les formes sont supprimées puis recréées plutôt que rapprochées du document une à une, le fichier
 * ne portant pas d'identifiant fiable, et dans UN lot transactionnel : un échec au milieu de
 * plusieurs appels laisserait un projet à moitié effacé.
 */
export default async function restoreProjectVersion(version: ProjectVersionModel): Promise<RestoreReport> {
	const projectStore = currentProjectEditorStore.get;
	const maps = projectStore.mapCollection.models.filter(map => !!map.id);

	const documents = await loadPublishedDocuments(version);
	// Sans les thèmes : la restauration réécrit les features, pas les thèmes du projet.
	const restoredMaps = pairMapsWithDocuments(maps, documents.maps);

	// Une carte que l'appariement laisse de côté serait vidée sans rien récupérer.
	if (restoredMaps.length !== maps.length) {
		throw new Error(
			`Publication ${version.number} : ses cartes ne correspondent pas à celles du projet, `
			+ `la restauration est impossible.`,
		);
	}

	const [featureIdsByMap, placeIds] = await Promise.all([
		Promise.all(maps.map(map => listMapFeatureIds(map.id))),
		listProjectPlaceIds(projectStore.project.id),
	]);

	const batch = new BatchManager();

	// Rejouées dans l'ordre : vider d'abord, sinon on cumulerait l'ancien et le nouveau. `id` est le
	// seul filtre que l'API accepte ici.
	featureIdsByMap.forEach(ids => {
		if (ids.length) batch.destroyWhere(FeatureModel, { id: ids });
	});

	const configurations: { map: MapModel; configuration: Record<string, unknown> }[] = [];

	const report: RestoreReport = {
		maps: restoredMaps.length,
		deleted: featureIdsByMap.flat().length,
		created: 0,
		unlinked: 0,
		configurationsFailed: 0,
	};

	restoredMaps.forEach(restoredMap => {
		const map = maps.find(m => String(m.id) === String(restoredMap.mapId));

		if (!map) return;

		const features = restoredMap.shapes.map(shape => {
			const attributes = featureAttributes(shape, map.urn, placeIds);

			if ((shape.entity as any)?.entity?.id !== undefined && attributes.entityUrn === null) {
				report.unlinked++;
			}

			return attributes;
		});

		// Comme `onSave` (`TokamapEditor/index.tsx`) : l'API refuse la création d'une forme à la fois.
		if (features.length) {
			batch.createMany(FeatureModel, features, { params: { map: map.id } });
			report.created += features.length;
		}

		const configuration = restoredConfiguration(restoredMap.json);

		if (configuration) configurations.push({ map, configuration });
	});

	try {
		await batch.run();
	} catch (err) {
		// L'API rend la première erreur du lot sans dire de quelle sous-requête elle vient.
		console.error('[restore] lot refusé', JSON.stringify(describeBatch(batch), null, 2), err);

		throw err;
	}

	report.configurationsFailed = await patchConfigurations(configurations);

	return report;
}

/**
 * Hors du lot : l'API refuse toute écriture sur une `Map` à travers `/_batch` (403 sur
 * `PATCH /maps/{id}`, 404 sur `/maps/batch`), alors que l'appel direct passe.
 *
 * Après le lot : si les formes n'avaient pas pu revenir, il ne faudrait pas avoir déjà recadré.
 */
async function patchConfigurations(
	configurations: { map: MapModel; configuration: Record<string, unknown> }[],
): Promise<number> {
	const results = await Promise.allSettled(
		configurations.map(({ map, configuration }) => map.patch({ configuration })),
	);

	const failed = results.filter(
		(result): result is PromiseRejectedResult => result.status === 'rejected',
	);

	failed.forEach(result => console.error('[restore] cadrage non rétabli', result.reason));

	return failed.length;
}

/** Le lot par nature d'opération, un exemplaire de chaque sous-requête, et les échecs. */
function describeBatch(batch: BatchManager) {
	const counts: Record<string, number> = {};
	const samples: Record<string, unknown> = {};
	const connector = batch.operations.at(0)?.model.connector as unknown as {
		servicePath?: string;
		subRequestHeaders?: Record<string, string>;
	} | undefined;

	batch.operations.forEach(operation => {
		const key = `${operation.method} ${operation.model.modelName}`;

		counts[key] = (counts[key] || 0) + 1;

		const item = operation.toRequestItem(connector?.servicePath || '', connector?.subRequestHeaders);

		samples[key] = { ...item, body: item.body?.slice(0, 300) ?? null };
	});

	// Renseigné seulement quand l'API a répondu opération par opération (207).
	const failed = batch.operations
		.filter(operation => operation.response && !operation.isSuccess)
		.map(operation => ({
			index: operation.index,
			operation: `${operation.method} ${operation.model.modelName}`,
			status: operation.status,
			detail: operation.errorMessage,
		}));

	return { counts, samples, failed };
}

/** Par `params` : `FeatureModel` ne déclare aucun filtre, alors que l'endpoint accepte bien `map`. */
async function listMapFeatureIds(mapId: id): Promise<id[]> {
	const collection = new ApiCollection(FeatureModel);

	await collection.list({ params: { map: mapId }, maxResults: MAX_FEATURES_PER_MAP });

	return collection.map(feature => feature.id);
}

async function listProjectPlaceIds(projectId: id): Promise<Set<string>> {
	const collection = new ApiCollection(PlaceModel);

	collection.setRequiredFilter('project', projectId);

	await collection.list({ maxResults: MAX_PLACES_PER_PROJECT });

	return new Set(collection.map(place => String(place.id)));
}
